import { useState } from 'react';
import type { Account, AccountStatus, AiTagView, Folder, FolderRole, MailboxRef, TagCount, UnifiedView } from '@momomail/shared';
import { folderName, timeAgo } from '../lib/format';
import { boxOf } from '../lib/screenRoutes';
import { Icon, type IconName } from './Icon';
import { t, tMessage } from '../lib/i18n';

const ROLE_ICON: Record<FolderRole, IconName> = {
  inbox: 'inbox',
  drafts: 'draft',
  sent: 'send',
  archive: 'archive',
  spam: 'spam',
  trash: 'trash',
};

function Count({ n, quiet }: { n: number; quiet?: boolean }) {
  if (!n) return null;
  return <span className={`count ${quiet ? 'count--quiet' : ''}`}>{n > 999 ? '999+' : n}</span>;
}

function sameRef(a: MailboxRef, b: MailboxRef) {
  if (a.kind === 'folder' && b.kind === 'folder') return a.folderId === b.folderId;
  if (a.kind === 'unified' && b.kind === 'unified') return a.view === b.view;
  if (a.kind === 'tag' && b.kind === 'tag') return a.tag === b.tag;
  return false;
}

/** An AI tag's name as a mailbox. */
export function tagViewLabel(tag: AiTagView): string {
  if (tag === 'important') return t('重要');
  if (tag === 'reply') return t('待回复');
  if (tag === 'low') return t('不重要');
  if (tag === 'junk') return t('垃圾');
  return tag.slice(6);
}

export function Sidebar({
  accounts,
  folders,
  statuses,
  counts,
  tagCounts,
  tagLabels,
  current,
  onSelect,
  onCompose,
  onAddAccount,
  onRetry,
  onDropMessages,
}: {
  accounts: Account[];
  folders: Folder[];
  statuses: Map<string, AccountStatus>;
  counts: Record<UnifiedView, number>;
  /** How much mail each AI tag has. */
  tagCounts: Partial<Record<AiTagView, TagCount>>;
  /** The user's own tags (settings), in their order. */
  tagLabels: string[];
  current: MailboxRef;
  onSelect: (ref: MailboxRef) => void;
  onCompose: () => void;
  onAddAccount: () => void;
  onRetry: (accountId: string) => void;
  onDropMessages: (folderId: number, ids: number[]) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('momomail.collapsed') ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const toggle = (id: string) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsed(next);
    try {
      localStorage.setItem('momomail.collapsed', JSON.stringify([...next]));
    } catch {
      /* fine if it can't be saved */
    }
  };
  const unified = (view: UnifiedView, label: string, icon: IconName, n: number) => {
    const ref: MailboxRef = { kind: 'unified', view };
    const active = sameRef(ref, current);
    return (
      <button className={`nav-item ${active ? 'is-active' : ''}`} data-afford-key={`folders.${boxOf(ref)}`} aria-current={active ? 'page' : undefined} onClick={() => onSelect(ref)}>
        <Icon name={icon} size={17} fill={view === 'flagged' && active} />
        <span className="nav-item__label">{label}</span>
        <Count n={n} quiet={view === 'flagged'} />
      </button>
    );
  };

  // The AI's tags as mailboxes (each with mail in it): important, needs a reply, the user's own tags, then low and junk
  const tagViews: AiTagView[] = (['important', 'reply', ...tagLabels.map((l) => `label:${l}` as const), 'low', 'junk'] as AiTagView[]).filter((v) => tagCounts[v]?.total);
  const tagIcon = (v: AiTagView): IconName => (v === 'important' ? 'zap' : v === 'reply' ? 'reply' : v === 'low' ? 'down' : v === 'junk' ? 'spam' : 'tag');
  const tags = tagViews.length > 0 && (
    <div className="sidebar__tags">
      <div className="sidebar__subheading">{t('AI 标签')}</div>
      {tagViews.map((v) => {
        const ref: MailboxRef = { kind: 'tag', tag: v };
        const active = sameRef(ref, current);
        const c = tagCounts[v]!;
        return (
          <button key={v} className={`nav-item nav-item--tag ${active ? 'is-active' : ''}`} data-afford-key={`folders.${boxOf(ref)}`} aria-current={active ? 'page' : undefined} onClick={() => onSelect(ref)}>
            <Icon name={tagIcon(v)} size={16} />
            <span className="nav-item__label">{tagViewLabel(v)}</span>
            {/* junk: how much there is to move; the others: what's unread */}
            <Count n={v === 'junk' ? c.total : c.unread} quiet={v === 'junk' || v === 'low'} />
          </button>
        );
      })}
    </div>
  );

  return (
    <nav
      className="sidebar"
      aria-label={t('邮箱和文件夹')}
      data-afford-key="mail.folders"
      data-afford-describe="The mailboxes: each account's folders (folders.folder-<id>), all inboxes, starred, the AI's tags. Press one to show its mail (or go to /mail/<box>)"
    >
      <button className="btn btn--primary sidebar__compose" data-afford-key="mail.compose" onClick={onCompose} title={t('写邮件（C）')}>
        <Icon name="pen" size={18} />
        {t('写邮件')}
      </button>

      <div className="sidebar__scroll">
        {accounts.length > 1 && (
          <section className="sidebar__group">
            <h3 className="sidebar__heading">{t('所有账户')}</h3>
            {unified('inbox', t('全部收件箱'), 'layers', counts.inbox)}
            {unified('flagged', t('星标邮件'), 'star', counts.flagged)}
            {tags}
          </section>
        )}
        {accounts.length === 1 && (
          <section className="sidebar__group">
            {unified('flagged', t('星标邮件'), 'star', counts.flagged)}
            {tags}
          </section>
        )}

        {accounts.map((account) => {
          const list = folders.filter((f) => f.accountId === account.id && f.selectable);
          const status = statuses.get(account.id);
          const open = !collapsed.has(account.id);
          const inboxUnread = list.find((f) => f.role === 'inbox')?.unread ?? 0;
          return (
            <section key={account.id} className="sidebar__group sidebar__account" style={{ '--acc': account.color } as React.CSSProperties}>
              <button className="account-head" aria-expanded={open} onClick={() => toggle(account.id)} title={account.email}>
                <span className="account-head__dot" />
                <span className="account-head__text">
                  <span className="account-head__name">{account.name}</span>
                  <span className="account-head__mail">{account.email}</span>
                </span>
                {!open && <Count n={inboxUnread} />}
                <Icon name="down" size={15} className={`account-head__chev ${open ? '' : 'is-closed'}`} />
              </button>
              {status?.state === 'error' && (
                <div className="account-error" role="alert">
                  <Icon name="alert" size={15} />
                  <span>{tMessage(status.error ?? '')}</span>
                  <button className="btn btn--small" onClick={() => onRetry(account.id)}>
                    {t('重试')}
                  </button>
                </div>
              )}
              {open && (
                <div className="folder-list">
                  {list.length === 0 && status?.state !== 'error' && <div className="folder-list__loading muted">{t('正在读取文件夹…')}</div>}
                  {list.map((f) => {
                    const ref: MailboxRef = { kind: 'folder', folderId: f.id };
                    const active = sameRef(ref, current);
                    return (
                      <button
                        key={f.id}
                        data-afford-key={`folders.${boxOf(ref)}`}
                        className={`nav-item ${active ? 'is-active' : ''} ${dropTarget === f.id ? 'is-drop' : ''}`}
                        style={{ paddingLeft: `${0.7 + Math.min(f.depth, 3) * 0.9}rem` }}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => onSelect(ref)}
                        onDragOver={(e) => {
                          if (!e.dataTransfer.types.includes('application/x-momomail-ids')) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setDropTarget(f.id);
                        }}
                        onDragLeave={() => setDropTarget((prev) => (prev === f.id ? null : prev))}
                        onDrop={(e) => {
                          setDropTarget(null);
                          const raw = e.dataTransfer.getData('application/x-momomail-ids');
                          if (!raw) return;
                          e.preventDefault();
                          onDropMessages(f.id, JSON.parse(raw) as number[]);
                        }}
                        title={f.path}
                      >
                        <Icon name={f.role ? ROLE_ICON[f.role] : 'folder'} size={17} />
                        <span className="nav-item__label">{folderName(f)}</span>
                        <Count n={f.role === 'drafts' ? f.total : f.role === 'sent' || f.role === 'trash' || f.role === 'archive' ? 0 : f.unread} quiet={f.role === 'drafts' || f.role === 'spam'} />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <footer className="sidebar__foot">
        <SyncLine accounts={accounts} statuses={statuses} />
        <button className="btn btn--small btn--quiet sidebar__add" onClick={onAddAccount}>
          <Icon name="plus" size={15} />
          {t('添加账户')}
        </button>
      </footer>
    </nav>
  );
}

/** Sync status line at the bottom: syncing / up to date · N minutes ago / offline. */
function SyncLine({ accounts, statuses }: { accounts: Account[]; statuses: Map<string, AccountStatus> }) {
  const list = accounts.map((a) => statuses.get(a.id)).filter((s): s is AccountStatus => !!s);
  if (!accounts.length) return null;
  const busy = list.some((s) => s.state === 'syncing' || s.state === 'connecting');
  const offline = list.filter((s) => s.state === 'offline' && s.error);
  const last = Math.max(0, ...list.map((s) => s.lastSync ?? 0));
  let text = t('等待连接…');
  let icon: IconName = 'refresh';
  let cls = '';
  if (busy) {
    text = t('正在收信…');
    cls = 'is-busy';
  } else if (offline.length) {
    text = t('离线，稍后自动重连');
    icon = 'offline';
    cls = 'is-offline';
  } else if (last) {
    text = t('已是最新 · {ago}', { ago: timeAgo(last) });
    icon = 'check';
    cls = 'is-ok';
  }
  const push = list.length > 0 && list.every((s) => s.push);
  return (
    <div className={`sync-line ${cls}`} title={push ? t('服务器支持推送（IDLE），新邮件会即时到达') : t('部分账户不支持推送，定时检查新邮件')}>
      <Icon name={icon} size={14} />
      <span>{text}</span>
      {push && !busy && <span className="sync-line__push">{t('即时')}</span>}
    </div>
  );
}
